import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import { useAthleteStore } from '@/store';
import { MatchListItem } from './components/MatchListItem';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/ui/PageShell';

export function MatchLogPage() {
    const activeId = useAthleteStore((s) => s.activeId);
    const navigate = useNavigate();

    const matches = useLiveQuery(() =>
        db.matches
            .where('athleteId')
            .equals(activeId)
            .reverse() // Newest first
            .sortBy('date')
        , [activeId]) ?? [];

    const wins = matches.filter(m => m.result === 'Win').length;
    const losses = matches.filter(m => m.result === 'Loss').length;
    const draws = matches.filter(m => m.result === 'Draw').length;

    const headerRight = (
        <button
            onClick={() => navigate('/matches/new')}
            className="w-10 h-10 rounded-full bg-gold-base text-bg-900 flex items-center justify-center font-bold shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:scale-105 transition-transform active:scale-95"
        >
            <Plus className="w-6 h-6" />
        </button>
    );

    return (
        <PageShell title="Match Log" subtitle="Track your record" right={headerRight}>
            <div className="space-y-6">
                {/* Quick Record Stats */}
                <div className="flex gap-4 px-4 mt-2">
                    <div className="bg-bg-elevated px-4 py-2 rounded-lg border border-white/5 flex-1 text-center">
                        <span className="block text-xs text-text-muted uppercase tracking-wider font-bold mb-1">Record</span>
                        <span className="text-xl font-black text-white">{wins}-{losses}{draws > 0 ? `-${draws}` : ''}</span>
                    </div>
                    <div className="bg-bg-elevated px-4 py-2 rounded-lg border border-white/5 flex-1 text-center">
                        <span className="block text-xs text-text-muted uppercase tracking-wider font-bold mb-1">Matches</span>
                        <span className="text-xl font-black text-white">{matches.length}</span>
                    </div>
                </div>

                {/* Filter Bar (Visual only for now) */}
                <div className="px-4 py-4 flex gap-2 overflow-x-auto no-scrollbar">
                    <button className="px-4 py-1.5 rounded-full bg-white/10 text-white text-sm font-medium whitespace-nowrap border border-white/10">All Matches</button>
                    <button className="px-4 py-1.5 rounded-full bg-transparent text-text-muted text-sm font-medium whitespace-nowrap border border-white/5 hover:bg-white/5">Wins</button>
                    <button className="px-4 py-1.5 rounded-full bg-transparent text-text-muted text-sm font-medium whitespace-nowrap border border-white/5 hover:bg-white/5">Losses</button>
                    <button className="px-4 py-1.5 rounded-full bg-transparent text-text-muted text-sm font-medium whitespace-nowrap border border-white/5 hover:bg-white/5">Tournaments</button>
                </div>

                {/* Match List */}
                <div className="px-4 space-y-3">
                    {matches.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                                <Trophy className="w-8 h-8 text-white/20" />
                            </div>
                            <h3 className="text-text-primary font-bold mb-2">No Matches Yet</h3>
                            <p className="text-text-muted text-sm max-w-[200px] mx-auto">
                                Log your competitive matches to track your record and meaningful stats.
                            </p>
                        </div>
                    ) : (
                        matches.map(match => (
                            <MatchListItem key={match.id} match={match} />
                        ))
                    )}
                </div>
            </div>
        </PageShell>
    );
}

function Trophy(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
            <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
            <path d="M4 22h16" />
            <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
            <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
            <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
        </svg>
    )
}
